
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import Sentry from '../lib/sentry';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);

    const applySentryUser = (u) => {
        if (u) {
            Sentry.setUser({ id: u.id, email: u.email });
            const customerId = u.app_metadata?.customer_ids?.[0];
            if (customerId) Sentry.setTag('customer_id', customerId);
        } else {
            Sentry.setUser(null);
        }
    };

    useEffect(() => {
        // 1. Get session on load
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            setUser(session?.user ?? null);
            applySentryUser(session?.user ?? null);
            setLoading(false);
        });

        // 2. Listen for changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
            setUser(session?.user ?? null);
            applySentryUser(session?.user ?? null);
            setLoading(false);
        });

        return () => subscription.unsubscribe();
    }, []);

    const login = async (email, password) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });
        if (error) throw error;
        return data;
    };

    const logout = async () => {
        const { error } = await supabase.auth.signOut({ scope: 'global' });
        if (error) throw error;
    };

    // Admin flag — mirrors the backend check (app_metadata.role === 'admin').
    // app_metadata is admin-controlled in Supabase, so it's safe to trust here
    // for UI gating (the backend independently enforces it on every request).
    const isAdmin = user?.app_metadata?.role === 'admin';

    // True if this user is allowed to see /projects (the ETL-ops page) — either
    // via admin role, or an explicit per-user opt-in flag. Mirrors the backend's
    // ProtectedRoute gate (requireMetadataKey="etl_admin_access") so UI entry
    // points can hide/redirect consistently with what the route will actually allow.
    const canAccessProjects = isAdmin || user?.app_metadata?.etl_admin_access === true;

    const value = {
        user,
        session,
        loading,
        isAdmin,
        canAccessProjects,
        login,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
